import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiCheck, FiPlus, FiSearch } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost } from '../utilities/stadiumsApi';
import { adminTeamsQuery, extractList, normalizeSeasonTeam } from '../utilities/tournamentsApi';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/seasonRoster.css';

const PAGE_SIZE = 40;
const SEARCH_DELAY_MS = 250;

export default function TeamPicker({ champId, isPicked, onPick }) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const timerRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    timerRef.current = setTimeout(() => setDebounced(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const fetchTeams = async (query, offset = 0) => {
    const requestId = ++requestRef.current;
    if (!champId) {
      setTeams([]);
      setHasMore(false);
      return [];
    }
    const response = await authorizedPost(dispatch, API_URLS.getCatalogTeams, adminTeamsQuery(champId, {
      search: query,
      limit: PAGE_SIZE,
      offset,
    }));
    if (requestId !== requestRef.current) return null;
    const list = extractList(response.data).map((team) => {
      const normalized = normalizeSeasonTeam(team);
      return { ...normalized, searchName: normalized.name.toLowerCase() };
    });
    if (offset === 0) setTeams(list);
    else setTeams((prev) => [...prev, ...list]);
    setHasMore(list.length >= PAGE_SIZE);
    return list;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTeams(debounced, 0)
      .catch(() => {
        if (!cancelled) {
          setTeams([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      requestRef.current += 1;
    };
  }, [debounced, champId]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchTeams(debounced, teams.length);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const searching = search !== debounced || loading;

  return (
    <div className="sr-picker">
      <div className="sr-picker-search">
        <FiSearch className="sr-picker-search-icon" />
        <input
          type="text"
          className="st-input"
          value={search}
          placeholder={t('trTeamSearchPh')}
          onChange={(event) => setSearch(event.target.value)}
          autoFocus
        />
      </div>

      {loading && teams.length === 0 ? (
        <div className="st-loading">
          <Loader type="Oval" color="#3ad1a0" height={32} width={32} />
        </div>
      ) : (
        <>
          <div className="sr-picker-list">
            {teams.length === 0 ? (
              <p className="sr-empty-text">{searching ? t('trTeamSearching') : t('trTeamsNotFound')}</p>
            ) : teams.map((team) => {
              const picked = isPicked(team.team_id);
              return (
                <button
                  type="button"
                  key={team.team_id}
                  className={`sr-picker-row ${picked ? 'is-picked' : ''}`}
                  onClick={() => !picked && onPick(team)}
                  disabled={picked}
                >
                  <img
                    src={getTeamImageUrl(team.image) || defaultTeamLogo}
                    alt=""
                    loading="lazy"
                    onError={(event) => { event.target.src = defaultTeamLogo; }}
                  />
                  <span className="sr-picker-name">{team.name}</span>
                  <span className="sr-picker-action">
                    {picked ? <FiCheck /> : <FiPlus />}
                  </span>
                </button>
              );
            })}
          </div>
          {teams.length > 0 ? (
            <div className="sr-picker-footer">
              <span>{t('trTeamsShown', { shown: teams.length, total: hasMore ? `${teams.length}+` : teams.length })}</span>
              {hasMore ? (
                <button
                  type="button"
                  className="tr-inline-link"
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? t('trTeamSearching') : t('trShowMore')}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
